import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type { Company, CompanyUser } from "../types";

export default function Companies() {
  const authApi = useAuthApi();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createSlug, setCreateSlug] = useState("");
  const [createJoinCode, setCreateJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserName, setAddUserName] = useState("");
  const [addUserRole, setAddUserRole] = useState("operator");
  const [addingUser, setAddingUser] = useState(false);
  const [userActionError, setUserActionError] = useState<string | null>(null);

  const loadCompanies = () => {
    setLoading(true);
    setError(null);
    authApi<Company[]>(`${PLATFORM}/companies`)
      .then((data) => setCompanies(data ?? []))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load companies"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authApi<Company[]>(`${PLATFORM}/companies`)
      .then((data) => {
        if (!cancelled) setCompanies(data ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load companies");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authApi]);

  const handleCreate = () => {
    setCreating(true);
    setCreateError(null);
    const body: Record<string, string> = {
      name: createName.trim(),
    };
    if (createSlug.trim()) body.slug = createSlug.trim();
    if (createJoinCode.trim()) body.join_code = createJoinCode.trim();

    authApi<Company>(`${PLATFORM}/companies`, {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then(() => {
        setCreateOpen(false);
        setCreateName("");
        setCreateSlug("");
        setCreateJoinCode("");
        loadCompanies();
      })
      .catch((e: unknown) => setCreateError(e instanceof Error ? e.message : "Failed to create company"))
      .finally(() => setCreating(false));
  };

  const loadCompanyUsers = (companyId: number) => {
    setUsersLoading(true);
    setUsersError(null);
    authApi<CompanyUser[]>(`${PLATFORM}/companies/${companyId}/users`)
      .then((data) => setCompanyUsers(data ?? []))
      .catch((e: unknown) => setUsersError(e instanceof Error ? e.message : "Failed to load company users"))
      .finally(() => setUsersLoading(false));
  };

  const openCompanyAccess = (company: Company) => {
    setSelectedCompany(company);
    setAddUserEmail("");
    setAddUserName("");
    setAddUserRole("operator");
    setUserActionError(null);
    setAccessOpen(true);
    loadCompanyUsers(company.id);
  };

  const handleAddCompanyUser = () => {
    if (!selectedCompany || !addUserEmail.trim()) return;
    setAddingUser(true);
    setUserActionError(null);
    authApi<CompanyUser>(`${PLATFORM}/companies/${selectedCompany.id}/users`, {
      method: "POST",
      body: JSON.stringify({
        email: addUserEmail.trim(),
        role: addUserRole,
        name: addUserName.trim() || undefined,
      }),
    })
      .then(() => {
        setAddUserEmail("");
        setAddUserName("");
        setAddUserRole("operator");
        loadCompanyUsers(selectedCompany.id);
      })
      .catch((e: unknown) => setUserActionError(e instanceof Error ? e.message : "Failed to add company user"))
      .finally(() => setAddingUser(false));
  };

  const handleChangeCompanyUserRole = (userId: number, currentRole: string) => {
    if (!selectedCompany) return;
    const newRole = currentRole === "admin" ? "operator" : "admin";
    setUserActionError(null);
    authApi<CompanyUser>(`${PLATFORM}/companies/${selectedCompany.id}/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ role: newRole }),
    })
      .then(() => loadCompanyUsers(selectedCompany.id))
      .catch((e: unknown) => setUserActionError(e instanceof Error ? e.message : "Failed to update role"));
  };

  const handleRemoveCompanyUser = (userId: number) => {
    if (!selectedCompany) return;
    setUserActionError(null);
    authApi(`${PLATFORM}/companies/${selectedCompany.id}/users/${userId}`, { method: "DELETE" })
      .then(() => loadCompanyUsers(selectedCompany.id))
      .catch((e: unknown) => setUserActionError(e instanceof Error ? e.message : "Failed to remove user"));
  };

  return (
    <Box>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
              Create Company
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Join Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Access</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell>{company.id}</TableCell>
                  <TableCell>{company.name}</TableCell>
                  <TableCell>{company.slug}</TableCell>
                  <TableCell>{company.join_code}</TableCell>
                  <TableCell>
                    <Chip
                      label={company.status}
                      color={company.status === "active" ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => openCompanyAccess(company)}>
                      Manage Users
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No companies found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      )}

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Company</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {createError && <Alert severity="error">{createError}</Alert>}
          <TextField
            label="Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            disabled={creating}
            fullWidth
          />
          <TextField
            label="Slug (optional)"
            value={createSlug}
            onChange={(e) => setCreateSlug(e.target.value)}
            disabled={creating}
            helperText="If empty, it is generated from the name."
            fullWidth
          />
          <TextField
            label="Join Code (optional)"
            value={createJoinCode}
            onChange={(e) => setCreateJoinCode(e.target.value)}
            disabled={creating}
            helperText="If empty, an 8-character code is generated."
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !createName.trim()}>
            {creating ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={accessOpen} onClose={() => !addingUser && setAccessOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Company Access — {selectedCompany?.name ?? "Company"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {(usersError || userActionError) && <Alert severity="error">{usersError ?? userActionError}</Alert>}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              label="Email"
              size="small"
              value={addUserEmail}
              onChange={(e) => setAddUserEmail(e.target.value)}
              disabled={addingUser}
              helperText="If the account exists, claims are synced immediately. Otherwise access becomes active after first login and re-invite."
              sx={{ minWidth: 280, flex: 1 }}
            />
            <TextField
              label="Name (optional)"
              size="small"
              value={addUserName}
              onChange={(e) => setAddUserName(e.target.value)}
              disabled={addingUser}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Select
              value={addUserRole}
              size="small"
              onChange={(e) => setAddUserRole(e.target.value)}
              disabled={addingUser}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
            </Select>
            <Button
              variant="contained"
              onClick={handleAddCompanyUser}
              disabled={addingUser || !addUserEmail.trim()}
            >
              {addingUser ? <CircularProgress size={16} /> : "Add User"}
            </Button>
          </Box>

          {usersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companyUsers.map((user) => {
                  const permissionGroup = user.permission_group ?? user.role ?? "operator";
                  return (
                    <TableRow key={user.id}>
                      <TableCell>{user.email ?? user.auth0_sub}</TableCell>
                      <TableCell>{user.name ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={permissionGroup}
                          color={permissionGroup === "admin" ? "primary" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={user.status} color={user.status === "active" ? "success" : "warning"} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleChangeCompanyUserRole(user.id, permissionGroup)}>
                          Make {permissionGroup === "admin" ? "Operator" : "Admin"}
                        </Button>
                        <Button size="small" color="error" onClick={() => handleRemoveCompanyUser(user.id)}>
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {companyUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No company users yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessOpen(false)} disabled={addingUser}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
